import { api } from '../../api/client';
import { store } from '../../state/store';
import type { CordobaOri, Source } from '../../api/types';
import { getLocalNorma, saveLocalNorma } from '../../utils/db-local';

export async function loadNorma(source: Source, id: string, ori?: CordobaOri, fresh = false, tituloHint?: string): Promise<void> {
  // 1. Intentar cargar desde caché local IndexedDB si no se requiere refresco explícito
  let cachedNorma: any = null;
  if (!fresh) {
    cachedNorma = await getLocalNorma(source, id);
    if (cachedNorma) {
      if (source === 'cordoba') cachedNorma.ori = ori ?? cachedNorma.ori ?? 'L';
      store.currentNorma.set(cachedNorma);
      store.loadingNorma.set(false);
      store.normaError.set(null);
      store.viewMode.set('html');
      store.drawerOpen.set(false);
      
      store.pushRecent({
        source,
        id,
        ori: cachedNorma.ori,
        numero: cachedNorma.meta.numero,
        tipo: cachedNorma.meta.tipo,
        titulo: cachedNorma.meta.titulo,
      });
      const hash = source === 'nacion'
        ? `#/n/${id}`
        : source === 'cordoba'
          ? `#/c/${cachedNorma.ori ?? 'L'}/${id}`
          : `#/b/${encodeURIComponent(id)}`;
      if (location.hash !== hash) history.replaceState(null, '', hash);
    }
  }

  // Si no había caché, mostramos cargando normalmente.
  // Si ya se cargó de caché, la consulta corre en segundo plano de manera silenciosa.
  if (!cachedNorma) {
    store.loadingNorma.set(true);
    store.normaError.set(null);
    store.viewMode.set('html');
    store.drawerOpen.set(false);
  }

  try {
    const norma =
      source === 'nacion'
        ? await api.getInfoleg(id, fresh)
        : source === 'cordoba'
          ? await api.getCordoba(ori ?? 'L', id, fresh)
          : await api.getBcraNorma(id, undefined, fresh);
    if (source === 'cordoba') norma.ori = ori ?? norma.ori ?? 'L';
    
    // Si el título que trajo la API es genérico o vacío, o parece un ID, usamos el hint
    const isIdLike = norma.meta.titulo && /^\d+$/.test(norma.meta.titulo);
    if (tituloHint && (!norma.meta.titulo || /^infoleg/i.test(norma.meta.titulo) || isIdLike)) {
      norma.meta.titulo = tituloHint;
    }

    store.currentNorma.set(norma);
    void saveLocalNorma(source, id, norma);

    // 2. Extraer número de ley real si no está en metadatos o es un ID interno (> 100000)
    let leyNumber = norma.meta.numero || '';
    const isInternalId = leyNumber && (parseInt(leyNumber) > 100000 || leyNumber === id);
    
    if (!leyNumber || isInternalId) {
      const leyMatch = norma.html.match(/Ley(?:\s+N.?)?\s+(\d+[\.\d+]*)/i);
      if (leyMatch) {
        leyNumber = leyMatch[1].replace(/\./g, '');
      }
    }

    store.pushRecent({
      source,
      id,
      ori: norma.ori,
      numero: leyNumber || norma.meta.numero,
      tipo: norma.meta.tipo,
      titulo: norma.meta.titulo,
    });
    const hash = source === 'nacion'
      ? `#/n/${id}`
      : source === 'cordoba'
        ? `#/c/${norma.ori ?? 'L'}/${id}`
        : `#/b/${encodeURIComponent(id)}`;
    if (location.hash !== hash) history.replaceState(null, '', hash);
  } catch (e) {
    if (!cachedNorma) {
      store.normaError.set((e as Error).message);
      store.currentNorma.set(null);
    } else {
      console.warn('Silent network update failed, using cached norm:', e);
    }
  } finally {
    if (!cachedNorma) {
      store.loadingNorma.set(false);
    }
  }
}

export async function refetchCurrent(fresh = true): Promise<void> {
  const cur = store.currentNorma.get();
  if (!cur) return;
  await loadNorma(cur.source, cur.id, cur.ori, fresh);
}

